import React, { useEffect, useState } from 'react';

import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { useMediaQuery, useTheme } from '@material-ui/core';

import {
  accountSelector,
  messageServiceStatusForAccountIdSelector,
} from '../../account/account-selectors';
import { loadAccount } from '../../account/actions/load-account';
import { loadMessageServicesStatuses } from '../../admin/actions/load-message-service-status';
import {
  EMPLOYEE_STATUS,
  loadOrgEmployeesByStatus,
} from '../../employees/actions/load-org-employees-by-status';
import { loadOrg } from '../../organization/actions/load-org';
import {
  employeesForGroupIdSelector,
  employeesForOrgIdSelector,
  employeesStateForOrgIdAndStatusSelector,
} from '../../reducers/employee-selectors';
import { orgSelector } from '../../reducers/org-selectors';
import { createAnnouncement } from '../actions/announcements/create-announcement';

import { TwilioBusinessInformationForm } from '../../account/components/twilio-business-information-form';
import { stateCityOptionalStepProvider } from '../../employees/city-state-provider';
import { issueFilters } from '../../employees/employee-list';
import { accountWillShowDeliverabilityMessage } from '../../organization/account-status-message';
import { ProductTelemetryController } from '../../product-telemetry/product-telemetry-controller';
import { OnMessageSendInput } from '../../typescript-types/messages/on-message-send-type';
import { UiButton } from '../../ui/ui-button';
import { UiDialog } from '../../ui/ui-dialog';
import { EmployeeMessageTemplates } from '../message-composer/employee-message-templates';

import { employeeLanguages } from '../../employees/utils/employee-languages';
import { allEmployeesKey } from '../../util/all-employees-option';
import { HELPSCOUT_ARTICLE_KEYS } from '../../util/helpscout-article-keys';
import { HelpScout } from '../../util/helpscout-beacon';
import { LanguageCode } from '../../util/languages';
import { TEMPLATE_TYPE } from '../../util/template-type';
import { confirm } from '../../widgets/confirmation-dialog';
import MessageWidget from '../../widgets/legacy-message';
import { MultipleLanguageFileUploadComposer } from '../../widgets/message-composer/wrappers/multiple-language-file-upload-composer';

const mapStateToProps = (state: any, { employeeGroupId, orgId }: InputProps) => {
  let employees: Array<Employee> = [];
  if (employeeGroupId === allEmployeesKey(orgId)) {
    employees = employeesForOrgIdSelector(state, orgId);
  } else {
    const numberGroupId = parseInt(String(employeeGroupId), 10);
    if (!Number.isNaN(numberGroupId)) {
      employees = employeesForGroupIdSelector(state, numberGroupId);
    }
  }
  const org = orgSelector(state, orgId);
  const account = org ? accountSelector(state, org.accountId) : null;
  const messageServiceStatuses = org
    ? messageServiceStatusForAccountIdSelector(state, org.accountId)
    : [];
  return {
    org,
    account,
    employees,
    employeesStateForOrgIdAndStatus: employeesStateForOrgIdAndStatusSelector(
      state,
      orgId,
      EMPLOYEE_STATUS.CURRENT,
    ),
    messageServiceStatuses,
  };
};

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { orgId }: InputProps,
) => ({
  actions: {
    createAnnouncement: (
      employeeGroupId: number | string,
      onMessageSendInput: OnMessageSendInput,
      hasVCardAttached: boolean,
      urlFileMappingsInfo?: UrlFileMappingInfo[],
    ) =>
      dispatch(
        createAnnouncement(
          orgId,
          employeeGroupId,
          onMessageSendInput.messageType,
          onMessageSendInput.languageToMessage,
          onMessageSendInput.languageToSubject,
          onMessageSendInput.sendMessageAt,
          hasVCardAttached,
          urlFileMappingsInfo,
        ),
      ),
    loadOrgEmployees: () => dispatch(loadOrgEmployeesByStatus(orgId, EMPLOYEE_STATUS.CURRENT)),
    loadOrg: () => dispatch(loadOrg(orgId)),
    loadAccount: (accountId: number) => dispatch(loadAccount(accountId)),
    loadMessageServiceStatus: (accountId: number) =>
      dispatch(loadMessageServicesStatuses(accountId)),
  },
});

type InputProps = {
  orgId: number;
  employeeGroupId: number | string;
  initialMessage?: string;
  onMessageSent?: () => void;
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & StateProps & DispatchProps;

export const EmployeeGroupSendAnnouncement = connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  stateCityOptionalStepProvider(
    (o) => o.phoneNumber,
    ({
      actions,
      orgId,
      org,
      account,
      employeeGroupId,
      employees,
      employeesStateForOrgIdAndStatus,
      messageServiceStatuses,
      initialMessage,
      onMessageSent,
    }: Props) => {
      const currentEmployees = employees.filter((e) => e.isCurrentEmployee);
      const { languageCounts, defaultLanguage, languagesToSupport } =
        employeeLanguages(currentEmployees);
      const initialLanguageToMessage: Record<LanguageCode, string> = {};
      languagesToSupport.forEach((language) => {
        initialLanguageToMessage[language] =
          language === defaultLanguage ? initialMessage || '' : '';
      });
      const [languageToMessage, setLanguageToMessage] = useState<Record<
        LanguageCode,
        string
      > | null>(initialLanguageToMessage);
      const [languageToSubject, setLanguageToSubject] = useState<Record<
        LanguageCode,
        string
      > | null>(null);
      const [messageType, setMessageType] = useState<string | null>(null);
      const [showEINForm, setShowEINForm] = useState<boolean>(false);

      const theme = useTheme();
      const isOnMobile = useMediaQuery(theme.breakpoints.down('xs'));

      useEffect(() => {
        actions.loadOrgEmployees();
        actions.loadOrg();
        if (org) {
          actions.loadAccount(org.accountId);
          actions.loadMessageServiceStatus(org.accountId);
        }
      });

      const handleMessageChange = (newLanguageToMessage: Record<LanguageCode, string>) => {
        setLanguageToMessage(newLanguageToMessage);
      };

      const handleSubjectChange = (newLanguageToSubject: Record<LanguageCode, string>) => {
        setLanguageToSubject(newLanguageToSubject);
      };

      const handleMessageTypeChange = (newMessageType: string) => {
        setMessageType(newMessageType);
      };

      const handleTemplateSelect = (selectedTemplate: string) => {
        setLanguageToMessage({
          ...languageToMessage,
          en: selectedTemplate,
        });
      };

      const handleSendMessage = async (onMessageSendInput: OnMessageSendInput) => {
        ProductTelemetryController.logEvent('clicked send employee announcement', {
          value: employeeGroupId,
          metadata: {
            isOnMobile: isOnMobile ? 'true' : 'false',
            isScheduled: onMessageSendInput.sendMessageAt ? 'true' : 'false',
          },
        });

        const urlFileMappingInput =
          onMessageSendInput.urlFileMappings &&
          onMessageSendInput.urlFileMappings.map((mapping) => ({
            organizationFileId: mapping.organizationFileId,
            dynamicMappingKey: mapping.urlKey,
            url: mapping.downloadableUrl || '', // Should never be empty string here
          }));

        if (account && accountWillShowDeliverabilityMessage(account)) {
          const willEnterEinNow = await confirm(
            'Cell service providers (e.g. AT&T, Verizon, etc) are going to prevent this announcement from being delivered to your recipients because your business is not yet registered to send text messages.',
            {
              cancelButtonLabel: 'Send Anyway',
              confirmButtonLabel: 'Start Registration',
              title: 'Delivery Problems Likely',
            },
          );
          if (willEnterEinNow) {
            setShowEINForm(true);
            return null;
          }
        }

        const hasVCardAttached = !!onMessageSendInput.sendWithVCard;
        return actions.createAnnouncement(
          employeeGroupId,
          onMessageSendInput,
          hasVCardAttached,
          urlFileMappingInput,
        );
      };

      const handleCloseEINForm = () => {
        setShowEINForm(false);
      };

      const handleArticleClick = () => {
        HelpScout.beacon.openArticle(HELPSCOUT_ARTICLE_KEYS.TEXT_MESSAGE_VARIABLES);
      };

      if (
        !employees ||
        !employeesStateForOrgIdAndStatus ||
        !employeesStateForOrgIdAndStatus.success
      ) {
        return null;
      }

      const employeesWithNoContactInfo = issueFilters.missingContactInfo.filter(currentEmployees);
      const link = issueFilters.missingContactInfo.link(orgId);

      const announcementIncludesVariables =
        (!languageToMessage && initialMessage?.includes('{{')) ||
        languageToMessage?.en?.includes('{{') ||
        languageToSubject?.en?.includes('{{');

      const thisOrgIsInProgress = (messageServiceStatuses || []).filter(
        (mss) => mss.status === 'IN_PROGRESS' && mss.orgId === orgId,
      ).length;

      return (
        <>
          {showEINForm && org ? (
            <UiDialog size="lg" onClose={handleCloseEINForm} title="Register your business">
              <UiDialog.Content>
                <TwilioBusinessInformationForm
                  accountId={org.accountId}
                  org={org}
                  onSave={handleCloseEINForm}
                />
              </UiDialog.Content>
            </UiDialog>
          ) : null}
          {thisOrgIsInProgress ? (
            <MessageWidget variant="warning" canBeDismissed={false} className="u-margin-bottom">
              <div className="typo-bold u-display-flex u-alignItems-center">
                Your registration to send text messages has been submitted. While it is in progress,
                it is still possible that cell service providers will prevent announcements from
                being delivered to a small number of participants. This will resolve when your
                registration completes.
              </div>
            </MessageWidget>
          ) : null}
          {currentEmployees.length === 0 && employeeGroupId !== allEmployeesKey(orgId) ? (
            // We shouldn't ever be displaying this message to our users because we filter out empty groups
            // However it is better to keep this message for user clarity than contend with an announcement sent to no one
            <div className="u-margin-top u-margin-bottom">
              <MessageWidget variant="warning" canBeDismissed={false}>
                This group is currently empty. To send an announcement, add some employees to the
                group or make another selection.
              </MessageWidget>
            </div>
          ) : (
            <>
              {employeesWithNoContactInfo.length > 0 ? (
                <div className="u-margin-top u-margin-bottom">
                  <MessageWidget variant="warning" canBeDismissed={false}>
                    <div className="u-spread">
                      <div>
                        <Link to={link}>
                          {employeesWithNoContactInfo.length}{' '}
                          {employeesWithNoContactInfo.length === 1 ? 'person has' : 'people have'}{' '}
                          no contact info and won&apos;t receive the announcement.
                        </Link>
                      </div>
                      <UiButton linkTo={link}>View Issues</UiButton>
                    </div>
                  </MessageWidget>
                </div>
              ) : null}
              {announcementIncludesVariables && (
                <div className="typo-subtitle">
                  Variables inside &#123;&#123;&#123; &#125;&#125;&#125; will be replaced by actual
                  values when the message is sent. Check out
                  <span role="presentation" className="typo-link" onClick={handleArticleClick}>
                    &nbsp;this article&nbsp;
                  </span>
                  for a list of variables you can use.
                </div>
              )}
              <MultipleLanguageFileUploadComposer
                orgId={orgId}
                languageToMessage={languageToMessage}
                languageCounts={languageCounts}
                languagesToSupport={languagesToSupport}
                initialMessage={initialMessage}
                defaultLanguage={defaultLanguage}
                enableEmail
                enableText
                messagePreviewEmployees={currentEmployees}
                messagePreviewType="announcement"
                autofocus
                canPersonalize
                canScheduleMessageInFuture
                allowSendWithVCard
                canTranslate
                onChange={handleMessageChange}
                onMessageSent={onMessageSent}
                onMessageTypeChange={handleMessageTypeChange}
                onSendMessage={handleSendMessage}
                onSubjectChange={handleSubjectChange}
                isOnMobile={isOnMobile}
              />
              <EmployeeMessageTemplates
                orgId={orgId}
                onSelect={handleTemplateSelect}
                templateType={TEMPLATE_TYPE.MESSAGE}
                messageType={messageType}
              />
            </>
          )}
        </>
      );
    },
  ),
);
